import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Plus, X, Trash2, Edit2, ClipboardCheck, Copy, MessageCircle,
  ChevronDown, Inbox, Clock, CheckCircle2, Send, Settings2, ShieldPlus, Calendar
} from "lucide-react";
import { authGet, authPost, authPut, authDelete, isAdvisor } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";

const SUM_OPTIONS = ["5 Lakh", "10 Lakh", "15 Lakh", "25 Lakh", "50 Lakh", "1 Crore"];
const DEDUCTIBLE_OPTIONS = ["Zero (no deductible)", "₹25,000", "₹50,000", "₹1 Lakh", "₹2 Lakh", "₹3 Lakh", "₹4 Lakh", "₹5 Lakh"];
const QUOTE_TYPE_OPTIONS = ["Fresh", "Portability"];
const STATUS_META = {
  new: { label: "New", color: "text-[#A87608]", bg: "bg-[#D4A017]/15" },
  in_progress: { label: "In Progress", color: "text-sky-600", bg: "bg-sky-500/15" },
  sent: { label: "Quotation Sent", color: "text-emerald-600", bg: "bg-emerald-500/15" },
};
const TABS = [
  { id: "all", label: "All", icon: Inbox, color: "#44506B" },
  { id: "new", label: "New", icon: Inbox, color: "#A87608" },
  { id: "in_progress", label: "In Progress", icon: Clock, color: "#0369a1" },
  { id: "sent", label: "Quotation Sent", icon: CheckCircle2, color: "#047857" },
];
const inputCls = "w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#33436B] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

// Normalize any pasted/entered date into DD/MM/YYYY
const toDDMMYYYY = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/); // ISO yyyy-mm-dd
  if (m) return `${m[3].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[1]}`;
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/); // dd-mm-yyyy / dd/mm/yyyy / dd.mm.yyyy
  if (m) return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3]}`;
  const d = s.replace(/\D/g, "");
  if (d.length === 8) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`; // ddmmyyyy typed straight
  return s;
};

// Editable DOB field: type OR paste OR pick from calendar. Formatting happens on blur
// (not on every keystroke) so the caret never jumps while typing the year.
const DobInput = ({ value, onChange, testId }) => {
  const handleChange = (e) => {
    // keep only digits and separators; do NOT reformat mid-typing
    onChange(e.target.value.replace(/[^0-9/.\-]/g, ""));
  };
  const handleBlur = (e) => {
    onChange(toDDMMYYYY(e.target.value));
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="relative w-full max-w-[170px]">
      <input
        className={`${inputCls} pr-9`}
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/YYYY"
        value={value || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        data-testid={testId}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center">
        <Calendar className="h-4 w-4 text-[#3A4557] pointer-events-none" />
        <input
          type="date"
          max={today}
          onChange={(e) => onChange(toDDMMYYYY(e.target.value))}
          className="absolute inset-0 opacity-0 cursor-pointer"
          tabIndex={-1}
          aria-label="Pick date of birth"
          data-testid={testId ? `${testId}-picker` : undefined}
        />
      </span>
    </div>
  );
};

const EMPTY_FORM = { client_name: "", pincode: "", notes: "", customSum: "" };

export default function QuotationRequests() {
  const navigate = useNavigate();
  const admin = isAdvisor();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [plans, setPlans] = useState([]);
  const [statusTab, setStatusTab] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  // request form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [members, setMembers] = useState([{ name: "", dob: "", ped: "" }]);
  const [selectedPlans, setSelectedPlans] = useState({});
  const [selectedSums, setSelectedSums] = useState([]);
  const [quoteTypes, setQuoteTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState(null);

  // plan manager form
  const [planForm, setPlanForm] = useState({ name: "", company: "", riders: [] });
  const [riderInput, setRiderInput] = useState("");
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [r1, r2] = await Promise.all([
        authGet("/api/advisor/quotation-requests"),
        authGet("/api/advisor/quote-plans"),
      ]);
      if (r1.ok) { setRequests(r1.data?.requests || []); setCounts(r1.data?.counts || {}); }
      if (r2.ok) setPlans(r2.data?.plans || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const toggleSum = (s) =>
    setSelectedSums((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const toggleQuoteType = (t) =>
    setQuoteTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const sumInsured = [...selectedSums, ...(form.customSum.trim() ? [form.customSum.trim()] : [])].join(", ");

  const togglePlan = (p) => setSelectedPlans((prev) => {
    const next = { ...prev };
    if (next[p.id]) delete next[p.id];
    else next[p.id] = { riders: [], deductibles: [] };
    return next;
  });

  const togglePlanRider = (pid, r) => setSelectedPlans((prev) => ({
    ...prev,
    [pid]: { ...prev[pid], riders: prev[pid].riders.includes(r) ? prev[pid].riders.filter((x) => x !== r) : [...prev[pid].riders, r] },
  }));

  const togglePlanDeductible = (pid, d) => setSelectedPlans((prev) => {
    const cur = prev[pid].deductibles || [];
    return {
      ...prev,
      [pid]: { ...prev[pid], deductibles: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d] },
    };
  });

  const resetForm = () => {
    setForm(EMPTY_FORM); setMembers([{ name: "", dob: "", ped: "" }]); setSelectedPlans({}); setSelectedSums([]); setQuoteTypes([]); setEditingRequestId(null);
  };

  const openEditRequest = (r) => {
    const tokens = (r.sum_insured || "").split(",").map((s) => s.trim()).filter(Boolean);
    const known = tokens.filter((t) => SUM_OPTIONS.includes(t));
    const custom = tokens.filter((t) => !SUM_OPTIONS.includes(t));
    setEditingRequestId(r.id);
    setForm({ client_name: r.client_name || "", pincode: r.pincode || "", notes: r.notes || "", customSum: custom.join(", ") });
    const ms = (r.members || []).map((m) => ({ name: m.name || "", dob: toDDMMYYYY(m.dob || ""), ped: m.ped || "" }));
    setMembers(ms.length ? ms : [{ name: "", dob: "", ped: "" }]);
    setSelectedSums(known);
    setQuoteTypes(r.quote_types || []);
    let reqPlans = r.plans || [];
    if (reqPlans.length === 0 && (r.plan_name || r.plan_id)) {
      reqPlans = [{ plan_id: r.plan_id, plan_name: r.plan_name, company: r.company, riders: r.riders || [], deductibles: r.deductibles || [] }];
    }
    const sp = {};
    reqPlans.forEach((rp) => {
      const match = plans.find((p) => p.id === rp.plan_id)
        || plans.find((p) => p.name === rp.plan_name && (p.company || "") === (rp.company || ""));
      if (match) sp[match.id] = { riders: rp.riders || [], deductibles: rp.deductibles || [] };
    });
    setSelectedPlans(sp);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const createRequest = async () => {
    if (!form.client_name.trim()) { toast.error("Enter the client name"); return; }
    const chosen = plans.filter((p) => selectedPlans[p.id]);
    if (chosen.length === 0) { toast.error("Select at least one company/plan"); return; }
    setSaving(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        pincode: form.pincode.trim(),
        plans: chosen.map((p) => ({ plan_id: p.id, plan_name: p.name, company: p.company, riders: selectedPlans[p.id].riders, deductibles: selectedPlans[p.id].deductibles || [] })),
        sum_insured: (sumInsured || "").trim(),
        quote_types: quoteTypes,
        members: members
          .filter((m) => m.name.trim() || m.dob || (m.ped || "").trim())
          .map((m) => ({ name: m.name.trim(), dob: toDDMMYYYY((m.dob || "").trim()), ped: (m.ped || "").trim() })),
        notes: form.notes.trim(),
      };
      const res = editingRequestId
        ? await authPut(`/api/advisor/quotation-requests/${editingRequestId}`, payload)
        : await authPost("/api/advisor/quotation-requests", payload);
      if (res.ok) {
        const savedId = editingRequestId || res.data?.request?.id || null;
        toast.success(editingRequestId ? "Request updated." : "Request sent to employee panel. Tap WhatsApp on the card to forward it too.");
        setShowForm(false); resetForm();
        await fetchAll();
        setExpandedId(savedId);
      } else toast.error(res.data?.detail || "Failed to save request");
    } catch { toast.error("Network error"); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await authPut(`/api/advisor/quotation-requests/${id}`, { status });
      if (res.ok) fetchAll();
      else toast.error(res.data?.detail || "Failed to update");
    } catch { toast.error("Network error"); }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Delete this quotation request?")) return;
    try {
      const res = await authDelete(`/api/advisor/quotation-requests/${id}`);
      if (res.ok) { toast.success("Deleted"); fetchAll(); }
      else toast.error(res.data?.detail || "Failed to delete");
    } catch { toast.error("Network error"); }
  };

  const copyMessage = (r) => { navigator.clipboard.writeText(r.message || ""); toast.success("Message copied"); };
  const whatsAppMessage = (r) => window.open(`https://wa.me/?text=${encodeURIComponent(r.message || "")}`, "_blank");

  // ---- plan manager ----
  const addRiderChip = () => {
    const v = riderInput.trim();
    if (!v) return;
    if (!planForm.riders.includes(v)) setPlanForm((p) => ({ ...p, riders: [...p.riders, v] }));
    setRiderInput("");
  };

  const savePlan = async () => {
    if (!planForm.name.trim()) { toast.error("Enter plan name"); return; }
    setPlanSaving(true);
    try {
      const payload = { name: planForm.name.trim(), company: planForm.company.trim(), riders: planForm.riders };
      const res = editingPlanId
        ? await authPut(`/api/advisor/quote-plans/${editingPlanId}`, payload)
        : await authPost("/api/advisor/quote-plans", payload);
      if (res.ok) {
        toast.success(editingPlanId ? "Plan updated" : "Plan added");
        setPlanForm({ name: "", company: "", riders: [] }); setEditingPlanId(null); setRiderInput("");
        fetchAll();
      } else toast.error(res.data?.detail || "Failed to save plan");
    } catch { toast.error("Network error"); }
    setPlanSaving(false);
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan from the catalog?")) return;
    try {
      const res = await authDelete(`/api/advisor/quote-plans/${id}`);
      if (res.ok) { toast.success("Plan deleted"); fetchAll(); }
      else toast.error(res.data?.detail || "Failed to delete");
    } catch { toast.error("Network error"); }
  };

  const visible = statusTab === "all" ? requests : requests.filter((r) => r.status === statusTab);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      <header className="bg-white/80 backdrop-blur-xl sticky top-0 z-40 border-b border-[#C9D4E6]">
        <div className="px-4 py-4 flex items-center gap-3 max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-[#3A4557] hover:text-[#1B2A4A]" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-[#1B2A4A] flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#A87608]" /> Quotation Requests
            </h1>
            <p className="text-[#3A4557] text-xs">{admin ? "Instruct your employee with a structured request" : "Quotations requested by admin — update status as you work"}</p>
          </div>
          {admin && view === "requests" && !showForm && (
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4A017] text-[#0a1628] text-sm font-semibold"
              data-testid="new-quotation-request-btn">
              <Plus className="h-4 w-4" /> New Request
            </button>
          )}
        </div>
        {admin && (
          <div className="px-4 pb-3 flex gap-2 max-w-4xl mx-auto">
            <button onClick={() => setView("requests")} data-testid="view-requests-tab"
              className={`px-4 py-1.5 rounded-full text-xs font-medium border ${view === "requests" ? "bg-[#D4A017] text-[#0a1628] border-[#D4A017]" : "text-[#2E3B57] border-[#C9D4E6]"}`}>
              Requests
            </button>
            <button onClick={() => setView("plans")} data-testid="view-plans-tab"
              className={`px-4 py-1.5 rounded-full text-xs font-medium border flex items-center gap-1 ${view === "plans" ? "bg-[#D4A017] text-[#0a1628] border-[#D4A017]" : "text-[#2E3B57] border-[#C9D4E6]"}`}>
              <Settings2 className="h-3 w-3" /> Plans & Riders ({plans.length})
            </button>
          </div>
        )}
      </header>

      <main className="px-4 py-5 max-w-4xl mx-auto space-y-5">
        {/* ===== NEW REQUEST FORM ===== */}
        {admin && view === "requests" && showForm && (
          <div className="glass-card p-5 space-y-5" data-testid="quotation-request-form">
            <div className="flex items-center justify-between">
              <h2 className="text-[#1B2A4A] font-semibold text-sm flex items-center gap-2"><Send className="h-4 w-4 text-[#A87608]" /> {editingRequestId ? "Edit Quotation Request" : "New Quotation Request"}</h2>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-[#3A4557] hover:text-[#1B2A4A]" data-testid="close-form-btn"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Client name *" value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="qr-client-name" />
              <input className={inputCls} placeholder="Pincode" inputMode="numeric" maxLength={6} value={form.pincode}
                onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })} data-testid="qr-pincode" />
            </div>

            {/* Members */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[#3A4557] text-xs">Members to be covered (name + DOB + PED)</label>
                <button type="button" onClick={() => setMembers([...members, { name: "", dob: "", ped: "" }])}
                  className="flex items-center gap-1 text-xs text-[#A87608] hover:underline" data-testid="qr-add-member-btn">
                  <Plus className="h-3 w-3" /> Add member
                </button>
              </div>
              <p className="text-[11px] text-[#5A6B8C] -mt-1 mb-2">DOB: type, paste, or tap the calendar. Format DD/MM/YYYY.</p>
              <div className="space-y-2.5">
                {members.map((m, i) => (
                  <div key={i} className="rounded-xl border border-[#C9D4E6] bg-white/70 p-2.5 space-y-2" data-testid={`qr-member-row-${i}`}>
                    <div className="flex gap-2 items-start">
                      <input className={inputCls} placeholder={`Member ${i + 1} name`} value={m.name}
                        onChange={(e) => setMembers(members.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))}
                        data-testid={`qr-member-name-${i}`} />
                      <DobInput
                        value={m.dob}
                        onChange={(val) => setMembers(members.map((x, idx) => (idx === i ? { ...x, dob: val } : x)))}
                        testId={`qr-member-dob-${i}`}
                      />
                      {members.length > 1 && (
                        <button type="button" onClick={() => setMembers(members.filter((_, idx) => idx !== i))}
                          className="text-[#3A4557] hover:text-red-600 px-1 pt-2.5" data-testid={`qr-remove-member-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-[#5A6B8C] uppercase tracking-wide shrink-0">PED</span>
                      <input className={inputCls} type="text"
                        placeholder="Pre-existing diseases — e.g. Diabetes, BP (optional)"
                        value={m.ped || ""}
                        onChange={(e) => setMembers(members.map((x, idx) => (idx === i ? { ...x, ped: e.target.value } : x)))}
                        data-testid={`qr-member-ped-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Plans (select multiple companies) */}
            <div>
              <label className="text-[#3A4557] text-xs block mb-2">
                Companies / Plans to quote * <span className="text-[#A87608] font-semibold">(select one or more)</span>
              </label>
              <div className="space-y-2" data-testid="qr-plans-list">
                {plans.map((p) => {
                  const sel = selectedPlans[p.id];
                  return (
                    <div key={p.id} className={`rounded-xl border transition-colors ${sel ? "border-[#D4A017] bg-[#D4A017]/5" : "border-[#C9D4E6] bg-white"}`}>
                      <button type="button" onClick={() => togglePlan(p)}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left"
                        data-testid={`qr-plan-toggle-${p.id}`}>
                        <span className={`h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${sel ? "bg-[#D4A017] border-[#D4A017] text-white" : "border-[#9DB0CB]"}`}>
                          {sel ? "✓" : ""}
                        </span>
                        <span className="text-sm font-semibold text-[#14213D]">{p.name}</span>
                        {p.company && <span className="text-xs text-[#3A4557]">— {p.company}</span>}
                      </button>
                      {sel && (
                        <div className="px-3 pb-3 space-y-2.5">
                          {(p.riders || []).length > 0 && (
                            <div>
                              <p className="text-[10px] font-semibold text-[#5A6B8C] uppercase tracking-wide mb-1.5">Riders / Add-ons</p>
                              <div className="flex flex-wrap gap-2" data-testid={`qr-riders-${p.id}`}>
                                {p.riders.map((r) => {
                                  const on = sel.riders.includes(r);
                                  return (
                                    <button key={r} type="button" onClick={() => togglePlanRider(p.id, r)}
                                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-[#D4A017] text-white border-[#D4A017]" : "text-[#2E3B57] border-[#C9D4E6] hover:border-[#D4A017]"}`}
                                      data-testid={`qr-rider-${p.id}-${r.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}>
                                      {on ? "✓ " : ""}{r}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div>
                            <p className="text-[10px] font-semibold text-[#5A6B8C] uppercase tracking-wide mb-1.5">Deductible <span className="normal-case text-[#8A97B4]">(optional · select one or more)</span></p>
                            <div className="flex flex-wrap gap-2" data-testid={`qr-deductible-${p.id}`}>
                              {DEDUCTIBLE_OPTIONS.map((d) => {
                                const on = (sel.deductibles || []).includes(d);
                                return (
                                  <button key={d} type="button" onClick={() => togglePlanDeductible(p.id, d)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-sky-500 text-white border-sky-500" : "text-[#2E3B57] border-[#C9D4E6] hover:border-sky-500"}`}
                                    data-testid={`qr-deductible-${p.id}-${d.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}>
                                    {on ? "✓ " : ""}{d}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {plans.length === 0 && <p className="text-xs text-[#3A4557] mt-1">No plans yet — add them in the "Plans & Riders" tab.</p>}
            </div>

            {/* Sum insured — multi-select */}
            <div>
              <label className="text-[#3A4557] text-xs block mb-2">Sum Insured <span className="text-[#5A6B8C]">(select one or more)</span></label>
              <div className="flex flex-wrap gap-2">
                {SUM_OPTIONS.map((s) => {
                  const on = selectedSums.includes(s);
                  return (
                    <button key={s} type="button" onClick={() => toggleSum(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${on ? "bg-emerald-500 text-white border-emerald-500" : "text-[#2E3B57] border-[#C9D4E6] hover:border-emerald-500/50"}`}
                      data-testid={`qr-sum-${s.replace(/\s/g, "-").toLowerCase()}`}>
                      {on ? "✓ " : ""}{s}
                    </button>
                  );
                })}
              </div>
              <input className={`${inputCls} mt-2 max-w-[260px]`} placeholder="Add custom (e.g. 75 Lakh, 2 Crore)" value={form.customSum}
                onChange={(e) => setForm({ ...form, customSum: e.target.value })} data-testid="qr-sum-custom-input" />
              {sumInsured && (
                <p className="text-[#41527A] text-xs mt-2" data-testid="qr-sum-preview">Selected: <span className="font-semibold text-[#1B2A4A]">{sumInsured}</span></p>
              )}
            </div>

            {/* Quote type — Fresh / Portability */}
            <div>
              <label className="text-[#3A4557] text-xs block mb-2">Quote For <span className="text-[#5A6B8C]">(select one or more)</span></label>
              <div className="flex flex-wrap gap-2" data-testid="qr-quote-types">
                {QUOTE_TYPE_OPTIONS.map((t) => {
                  const on = quoteTypes.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleQuoteType(t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-violet-500 text-white border-violet-500" : "text-[#2E3B57] border-[#C9D4E6] hover:border-violet-500"}`}
                      data-testid={`qr-quote-type-${t.toLowerCase()}`}>
                      {on ? "✓ " : ""}{t}
                    </button>
                  );
                })}
              </div>
            </div>

            <textarea className={inputCls} rows={2} placeholder="Extra instructions (optional) — e.g. compare with existing policy, deductible preference"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="qr-notes" />

            <button onClick={createRequest} disabled={saving}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] text-[#0a1628] font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="qr-submit-btn">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} {editingRequestId ? "Update Request" : "Send Request to Employee"}
            </button>
          </div>
        )}

        {/* ===== REQUESTS LIST ===== */}
        {view === "requests" && (
          <>
            <Swipeable className="flex gap-2 pb-1">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = statusTab === t.id;
                const count = t.id === "all" ? counts.total : counts[t.id];
                return (
                  <button key={t.id} onClick={() => setStatusTab(t.id)} data-testid={`qr-tab-${t.id}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap border"
                    style={{ backgroundColor: active ? t.color : "transparent", color: active ? "#ffffff" : t.color, borderColor: active ? t.color : "#C9D4E6" }}>
                    <Icon className="h-4 w-4" /> {t.label}
                    <span className={`px-1.5 rounded-full text-xs ${active ? "bg-black/15" : "bg-[#EDF1F8]"}`}>{count ?? 0}</span>
                  </button>
                );
              })}
            </Swipeable>

            {visible.length === 0 ? (
              <div className="text-center py-14 text-[#3A4557]" data-testid="no-requests">
                <ClipboardCheck className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">{admin ? 'No requests yet. Tap "New Request" to instruct your employee.' : "No quotation requests yet."}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {visible.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.new;
                  const expanded = expandedId === r.id;
                  return (
                    <div key={r.id} className="glass-card overflow-hidden" data-testid={`qr-card-${r.id}`}>
                      <button type="button" onClick={() => setExpandedId(expanded ? null : r.id)}
                        className="w-full flex items-center gap-3 p-4 text-left" data-testid={`qr-toggle-${r.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[#1B2A4A] font-semibold text-sm">{r.client_name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.bg} ${meta.color}`}>{meta.label}</span>
                          </div>
                          <p className="text-[#3A4557] text-xs mt-1 truncate">
                            {r.plan_name}{r.company ? ` (${r.company})` : ""}{r.sum_insured ? ` · ${r.sum_insured}` : ""} · {(r.members || []).length} member{(r.members || []).length === 1 ? "" : "s"} · {fmtDate(r.created_at)}
                          </p>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-[#3A4557] transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`} />
                      </button>

                      {expanded && (
                        <div className="border-t border-[#C9D4E6] p-4 space-y-4">
                          <pre className="whitespace-pre-wrap text-xs text-[#2E3B57] bg-[#EDF1F8]/30 border border-[#C9D4E6] rounded-xl p-3 font-sans" data-testid={`qr-message-${r.id}`}>
                            {r.message}
                          </pre>
                          <div className="flex items-center gap-2 flex-wrap">
                            <select value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)}
                              className="h-9 px-3 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-xs"
                              data-testid={`qr-status-select-${r.id}`}>
                              <option value="new">New</option>
                              <option value="in_progress">In Progress</option>
                              <option value="sent">Quotation Sent</option>
                            </select>
                            <button onClick={() => copyMessage(r)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] text-[#1B2A4A] text-xs font-medium" data-testid={`qr-copy-${r.id}`}>
                              <Copy className="h-3.5 w-3.5" /> Copy
                            </button>
                            <button onClick={() => whatsAppMessage(r)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white text-xs font-medium" data-testid={`qr-whatsapp-${r.id}`}>
                              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                            </button>
                            {admin && (
                              <button onClick={() => openEditRequest(r)} className="flex items-center gap-1 text-xs text-[#0369a1] hover:text-[#075985] ml-auto" data-testid={`qr-edit-${r.id}`}>
                                <Edit2 className="h-3.5 w-3.5" /> Edit
                              </button>
                            )}
                            {admin && (
                              <button onClick={() => deleteRequest(r.id)} className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700" data-testid={`qr-delete-${r.id}`}>
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ===== PLANS & RIDERS MANAGER (admin) ===== */}
        {admin && view === "plans" && (
          <>
            <div className="glass-card p-5 space-y-3" data-testid="plan-manager-form">
              <h2 className="text-[#1B2A4A] font-semibold text-sm flex items-center gap-2">
                <ShieldPlus className="h-4 w-4 text-[#A87608]" /> {editingPlanId ? "Edit Plan" : "Add Plan"}
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Plan name * (e.g. Optima Secure)" value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} data-testid="plan-name-input" />
                <input className={inputCls} placeholder="Company (e.g. HDFC ERGO)" value={planForm.company}
                  onChange={(e) => setPlanForm({ ...planForm, company: e.target.value })} data-testid="plan-company-input" />
              </div>
              <div>
                <div className="flex gap-2">
                  <input className={inputCls} placeholder="Add a rider and press +" value={riderInput}
                    onChange={(e) => setRiderInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRiderChip())}
                    data-testid="rider-input" />
                  <button type="button" onClick={addRiderChip} className="h-10 px-3 bg-[#EDF1F8] hover:bg-[#E2E9F4] rounded-xl text-[#1B2A4A]" data-testid="add-rider-btn">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {planForm.riders.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {planForm.riders.map((r) => (
                      <span key={r} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D4A017]/15 text-[#A87608] text-xs" data-testid={`rider-chip-${r.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}>
                        {r}
                        <button type="button" onClick={() => setPlanForm({ ...planForm, riders: planForm.riders.filter((x) => x !== r) })}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={savePlan} disabled={planSaving}
                  className="px-5 py-2.5 rounded-xl bg-[#D4A017] text-[#0a1628] text-sm font-semibold disabled:opacity-60" data-testid="save-plan-btn">
                  {planSaving ? "Saving…" : editingPlanId ? "Update Plan" : "Add Plan"}
                </button>
                {editingPlanId && (
                  <button onClick={() => { setEditingPlanId(null); setPlanForm({ name: "", company: "", riders: [] }); setRiderInput(""); }}
                    className="px-4 py-2.5 rounded-xl border border-[#C9D4E6] text-[#2E3B57] text-sm" data-testid="cancel-plan-edit-btn">
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {plans.map((p) => (
                <div key={p.id} className="glass-card p-4" data-testid={`plan-card-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[#1B2A4A] font-semibold text-sm">{p.name}{p.company && <span className="text-[#3A4557] font-normal"> — {p.company}</span>}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(p.riders || []).length === 0 && <span className="text-xs text-[#3A4557]">No riders added</span>}
                        {(p.riders || []).map((r) => (
                          <span key={r} className="px-2 py-0.5 rounded-full bg-[#EDF1F8] text-[#2E3B57] text-xs">{r}</span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => { setEditingPlanId(p.id); setPlanForm({ name: p.name, company: p.company || "", riders: [...(p.riders || [])] }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className="p-2 rounded-lg hover:bg-[#EDF1F8] text-[#3A4557] hover:text-[#A87608]" data-testid={`edit-plan-${p.id}`}>
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => deletePlan(p.id)} className="p-2 rounded-lg hover:bg-[#EDF1F8] text-[#3A4557] hover:text-red-600" data-testid={`delete-plan-${p.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
      <MobileBottomNav />
    </div>
  );
}
